"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "ui";

type Props = {
  initialContent: string;
  onSave: (content: string) => void;
  onCancel: () => void;
};

function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent ?? "";
}

function wrapInHtml(text: string): string {
  return text
    .split("\n")
    .map((line) => `<p>${line || ""}</p>`)
    .join("");
}

export default function InlineEditor({
  initialContent,
  onSave,
  onCancel,
}: Props) {
  const [value, setValue] = useState(() => stripHtml(initialContent));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
    textareaRef.current?.select();
  }, []);

  const handleSave = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) onSave(wrapInHtml(trimmed));
  }, [value, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Escape") {
        onCancel();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave, onCancel],
  );

  const isEmpty = value.trim().length === 0;

  return (
    <div className="mt-1">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        rows={1}
        className="block w-full resize-none bg-transparent text-sm leading-snug text-foreground/90 outline-none"
      />
      <div className="mt-2 flex items-center gap-2">
        <Button type="button" size="sm" onClick={handleSave} disabled={isEmpty}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <span className="ml-1 hidden text-xs text-muted-foreground sm:inline">
          Press <kbd className="rounded bg-muted px-1">Enter</kbd> +{" "}
          <kbd className="rounded bg-muted px-1">⌘</kbd>/
          <kbd className="rounded bg-muted px-1">Ctrl</kbd> to save,{" "}
          <kbd className="rounded bg-muted px-1">Esc</kbd> to cancel
        </span>
      </div>
    </div>
  );
}
